import { Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, Step, StepLabel, Stepper, Tab, Tabs } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CancelIcon from '@material-ui/icons/Cancel';
import SaveIcon from '@material-ui/icons/Save';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ISubmenuArray, Submenu } from '../../comps/Submenu';
import { OcEdit } from '../../main/OcEdit';
import { transl } from '../../main/transl';
import { toasterSvc } from '../../services/ToasterSvc';
import { useOmnistore } from '../../stores/Omnistore';
import { mg } from '../mg';
import { useBooleanStateAgg, useNumberStateAgg } from '../useStateAgg';
import { MgColumnExtras as IMgColumnExtras, MgColumnist } from './MgColumn';

const useStyles = makeStyles({
	MgEntityEditDialog: {
		'& .MuiDialog-paper': {
			minWidth: '40%',
			minHeight: '80%',
		},
	},
	stepper: {
		width: '100%',
	},
	tabs: {
		width: '100%',
		'& .MuiTab-root': {
			flex: 1,
		},
	},
	Content: {
		marginTop: 20,
		display: 'grid',
		gridTemplateColumns: 'auto',
	},
	block: {
		gridRow: 1,
		gridColumn: 1,
		display: 'grid',
		gridTemplateColumns: 'auto',
		alignContent: 'start',
	},
	hiddenBlock: {
		opacity: 0,
		zIndex: -1,
	},
	error: {
		color: 'red',
	},
});

interface IContentProps {
	entity: Record<string, any>
	columnist: MgColumnist
	extras: Record<string, IMgColumnExtras>
	step: number
	showErrors: boolean
}

const Content = observer(function Content(props: IContentProps) {
	const classes = useStyles();
	const {entity, columnist, extras, step, showErrors} = props;
	return <div className={classes.Content}>
		{columnist.blocks.map((b, i) => <div key={i} className={mg.clsn(i!==step && classes.hiddenBlock, classes.block)}>
			{b.map((c, i) => <OcEdit key={i}
				column={c}
				entity={entity}
				extras={extras[c.name]}
				showErrors={showErrors}
			/>)}
		</div>)}
	</div>;
});

interface IMgEntityEditDialogProps {
	columnist: MgColumnist
	extras: Record<string, IMgColumnExtras>
	entity: Record<string, any>
	baseName: string
	finish(entity?: Record<string, any>) : void
	readonly?: boolean
	children?: ISubmenuArray
}

export const MgEntityEditDialog = observer(function MgEntityEditDialog(props: IMgEntityEditDialogProps) {
	const classes = useStyles();
	const stepAgg = useNumberStateAgg(0);
	const {columnist, extras, entity, baseName, finish, readonly, children} = props;
	const isUpdate = !!entity.id;
	const omnistore = useOmnistore();
	const blockErrors = columnist.getBlockErrors(stepAgg.value, extras);
	const showErrorsAgg = useBooleanStateAgg();
	function prev() {
		showErrorsAgg.falseHandler();
		if (!isUpdate && stepAgg.value) {
			stepAgg.setter(stepAgg.value-1);
		} else {
			finish();
		}
	}
	function next() {
		showErrorsAgg.setter(!!blockErrors.length);
		if (blockErrors.length && !omnistore.reoctoStore.ignoreProblems) {
			toasterSvc.error('pleaseFixItFirst', {message: transl('pleaseFixItFirst', transl(blockErrors[0].label))});
		} else if (isUpdate || stepAgg.value===columnist.length-1) {
			finish(entity);
		} else {
			stepAgg.setter(stepAgg.value+1);
		}
	}
	return <Dialog open={true} fullWidth className={classes.MgEntityEditDialog}>
		<DialogTitle>{transl(baseName + '_' + (isUpdate ? 'update' : 'create'))}</DialogTitle>
		<DialogContent>
			{!children ? null : <Submenu>{children}</Submenu>}
			{isUpdate
				? <Tabs value={stepAgg.value} onChange={(e, v) => stepAgg.setter(v)} className={classes.tabs}>
					{columnist.blocks.map((s, i) => <Tab key={i} label={transl(baseName + '_' + i)}/>)}
				</Tabs>
				: <Stepper activeStep={stepAgg.value} className={classes.stepper}>
					{columnist.blocks.map((s, i) => <Step key={i}><StepLabel>{transl(baseName + '_' + i)}</StepLabel></Step>)}
				</Stepper>
			}
			<Content columnist={columnist} extras={extras} entity={entity} step={stepAgg.value} showErrors={showErrorsAgg.value}/>
		</DialogContent>
		<DialogActions>
			<Button
				startIcon={!isUpdate && stepAgg.value ? <ArrowBackIcon/> : <CancelIcon/>}
				onClick={prev}
			>
				{transl(!isUpdate && stepAgg.value ? 'back' : 'cancel')}
			</Button>
			{readonly ? null : <Button
				color="primary"
				className={showErrorsAgg.value && blockErrors.length ? classes.error : ''}
				startIcon={isUpdate ? <SaveIcon/> : stepAgg.value===columnist.length-1 ? <SaveIcon/> : <ArrowForwardIcon/>}
				onClick={next}
			>
				{transl(isUpdate ? 'save' : stepAgg.value===columnist.length-1 ? 'finish' : 'next')}
			</Button>
			}
		</DialogActions>
	</Dialog>;
});
